/**
 * AI Calling admin/org schema — language, voice, infields, outcomes.
 * Runtime create/initiate use this config or the current form only.
 * Official catalogs are admin-editable defaults, never silent fallbacks.
 */

import {
  AI_CALL_TYPES,
  SCREENING_CREATE_INFIELD_KEYS,
  DRIVE_CREATE_INFIELD_KEYS,
  SCREENING_CREATE_OUTCOMES,
  DRIVE_CREATE_OUTCOMES,
  INDUS_SCHEMA_SLOT_LIMIT,
  normalizeCallType,
  normalizeTranscriptLanguage,
  canonicalizeIndusVoiceId,
  languageFromIndusVoice,
  buildOfficialCallInfield,
  hasStoredAgentVoice,
} from '@/utils/ai-calling-prompt-defaults'
import {
  getCreateInfieldsForCallType,
  getCreateOutcomesForCallType,
  getSchemaSlotLimit,
} from '@/utils/ai-calling-catalog'

export const ADMIN_DEFAULT_SCREENING_INFIELDS = [...SCREENING_CREATE_INFIELD_KEYS]
export const ADMIN_DEFAULT_DRIVE_INFIELDS = [...DRIVE_CREATE_INFIELD_KEYS]
export const ADMIN_DEFAULT_SCREENING_OUTCOMES = SCREENING_CREATE_OUTCOMES.map(item => ({ ...item }))
export const ADMIN_DEFAULT_DRIVE_OUTCOMES = DRIVE_CREATE_OUTCOMES.map(item => ({ ...item }))

/** Empty until Admin Settings saves aliases. Never invent client↔client_name. */
export const ADMIN_DEFAULT_INFIELD_ALIASES = {}

export const AI_CALLING_CONFIG_MESSAGES = {
  createName: 'Cannot create the agent — name is required.',
  createLanguage: 'Cannot create the agent — language is not set. Choose a language in Admin Settings or the create form.',
  createLanguageMulti: 'Cannot create the agent — language cannot be Multi. Choose a transcript language (English, Hindi, …).',
  createVoice: 'Cannot create the agent — voice is not set. Choose a voice in Admin Settings or the create form.',
  createInfields: 'Cannot create the agent — infields schema is empty. Configure infields in AI Calling settings.',
  createOutcomes: 'Cannot create the agent — outcomes schema is empty. Configure outcomes in AI Calling settings.',
  createInfieldsLimit: `Cannot create the agent — more than ${INDUS_SCHEMA_SLOT_LIMIT} infields. Indus allows a maximum of ${INDUS_SCHEMA_SLOT_LIMIT}.`,
  createOutcomesLimit: `Cannot create the agent — more than ${INDUS_SCHEMA_SLOT_LIMIT} outcomes. Indus allows a maximum of ${INDUS_SCHEMA_SLOT_LIMIT}.`,
  createScript: 'Cannot create the agent — opening line and script are required. Set them on the create form or in Admin Settings.',
  initiateAgent: 'Cannot start the call — agent is not set. Pick an agent on this job (do not rely on a hidden org default).',
  initiateDid: 'Cannot start the call — outbound number (DID) is not set. Configure number1/number2 in AI Calling settings.',
  initiatePhone: 'Cannot start the call — enter a valid candidate phone number.',
  initiateLanguage: 'Cannot start the call — language is not set. Set language on this agent in Admin Settings or the call form.',
  initiateLanguageMulti: 'Cannot start the call — language cannot be Multi. Set a transcript language (English, Hindi, …).',
  initiateInfields: 'Cannot start the call — infields schema is empty. Configure infields in AI Calling settings.',
  initiateRequired: 'Cannot start the call — missing required infield values',
}

export class AiCallingConfigError extends Error {
  constructor(message, extra = {}) {
    super(message)
    this.name = 'AiCallingConfigError'
    this.code = extra.code || 'AI_CALLING_CONFIG'
    this.httpStatus = extra.httpStatus || 400
    this.errors = extra.errors || [message]
    this.missing = extra.missing || []
  }
}

export function isTranscriptLanguageCode(value) {
  const n = normalizeTranscriptLanguage(value)
  return !!(n && n !== 'multi')
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function firstNonEmpty(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const text = String(values[i] == null ? '' : values[i]).trim()
    if (text) return text
  }
  return ''
}

function fieldNameOf(item) {
  if (typeof item === 'string') return item.trim()
  if (!item || typeof item !== 'object') return ''
  return String(
    item.field_name || item.outcome_name || item.name || item.key || item.field || '',
  ).trim()
}

function asKeyList(raw) {
  if (!raw) return []
  if (typeof raw === 'string') {
    return raw.split(/[\n,]+/).map(part => part.trim()).filter(Boolean)
  }
  if (!Array.isArray(raw)) {
    if (typeof raw === 'object') {
      return Object.keys(raw).map(k => String(k || '').trim()).filter(Boolean)
    }
    return []
  }
  const keys = []
  const seen = new Set()
  raw.forEach((item) => {
    const name = fieldNameOf(item)
    if (!name || seen.has(name)) return
    seen.add(name)
    keys.push(name)
  })
  return keys
}

function asOutcomeList(raw) {
  if (!raw) return []
  if (!Array.isArray(raw)) return []
  const list = []
  const seen = new Set()
  raw.forEach((item) => {
    if (typeof item === 'string') {
      const name = item.trim()
      if (!name || seen.has(name)) return
      seen.add(name)
      list.push({
        outcome_name: name,
        outcome_description: name,
        outcome_type: 'TEXT',
        outcome_enum: null,
        is_visible: true,
      })
      return
    }
    if (!item || typeof item !== 'object') return
    const name = fieldNameOf(item)
    if (!name || seen.has(name)) return
    seen.add(name)
    list.push({
      outcome_name: name,
      outcome_description: String(item.outcome_description || item.description || name).trim() || name,
      outcome_type: String(item.outcome_type || item.type || 'TEXT').toUpperCase() || 'TEXT',
      outcome_enum: Array.isArray(item.outcome_enum) ? item.outcome_enum : (item.outcome_enum || null),
      is_visible: item.is_visible !== false,
    })
  })
  return list
}

function pickTypedList(src, callType, screeningKeys, driveKeys, flatKeys) {
  const bag = asObject(src)
  const screening = bag.screening || bag.fresh || bag[AI_CALL_TYPES.FRESH]
  const drive = bag.drive || bag[AI_CALL_TYPES.DRIVE]
  if (screening || drive) {
    return normalizeCallType(callType) === AI_CALL_TYPES.DRIVE ? drive : screening
  }
  if (normalizeCallType(callType) === AI_CALL_TYPES.DRIVE && driveKeys) return driveKeys
  if (screeningKeys) return screeningKeys
  return flatKeys
}

export function parseInfieldAliases(raw) {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  const map = {}
  Object.keys(src).forEach((key) => {
    const name = String(key || '').trim()
    if (!name) return
    const value = src[key]
    const aliases = Array.isArray(value)
      ? value.map(v => String(v || '').trim()).filter(Boolean)
      : String(value || '').split(/[\n,]+/).map(v => v.trim()).filter(Boolean)
    if (!aliases.length) return
    map[name] = aliases
  })
  return map
}

/**
 * Normalize AI Calling admin/org config (infields, outcomes, language, voice).
 * Missing lists stay empty — callers must fail visibly, not invent catalogs.
 */
export function parseAiCallingSchemaConfig(raw = {}) {
  const src = asObject(raw)
  const nested = asObject(src.data)
  const bag = { ...src, ...nested }
  const infieldsRaw = pickTypedList(
    bag.infields || bag.call_infields || bag.call_infield,
    bag.callType || bag.call_type,
    bag.screeningInfields || bag.screening_infields,
    bag.driveInfields || bag.drive_infields,
    bag.infields || bag.call_infields,
  )
  const driveInfieldsRaw = bag.driveInfields || bag.drive_infields
    || (asObject(bag.infields).drive)
  const screeningInfieldsRaw = bag.screeningInfields || bag.screening_infields
    || (asObject(bag.infields).screening)
    || (asObject(bag.infields).fresh)
    || (Array.isArray(bag.infields) ? bag.infields : null)
  const outcomesRaw = pickTypedList(
    bag.outcomes || bag.call_outcomes || bag.call_outcome,
    bag.callType || bag.call_type,
    bag.screeningOutcomes || bag.screening_outcomes,
    bag.driveOutcomes || bag.drive_outcomes,
    bag.outcomes || bag.call_outcomes,
  )
  const screeningOutcomesRaw = bag.screeningOutcomes || bag.screening_outcomes
    || (asObject(bag.outcomes).screening)
    || (asObject(bag.outcomes).fresh)
    || (Array.isArray(bag.outcomes) ? bag.outcomes : null)
  const driveOutcomesRaw = bag.driveOutcomes || bag.drive_outcomes
    || (asObject(bag.outcomes).drive)

  const language = normalizeTranscriptLanguage(
    bag.transcriptLanguage || bag.transcript_language || bag.language || bag.defaultLanguage,
  )
  const voice = canonicalizeIndusVoiceId(
    bag.voice || bag.voice_id || bag.defaultVoice || bag.default_voice,
  )

  return {
    language: language === 'multi' ? '' : language,
    voice,
    screeningInfields: asKeyList(screeningInfieldsRaw),
    driveInfields: asKeyList(driveInfieldsRaw),
    infields: asKeyList(infieldsRaw),
    screeningOutcomes: asOutcomeList(screeningOutcomesRaw),
    driveOutcomes: asOutcomeList(driveOutcomesRaw),
    outcomes: asOutcomeList(outcomesRaw),
    infieldAliases: parseInfieldAliases(
      bag.infieldAliases || bag.infield_aliases || bag.aliases,
    ),
    requiredInfields: asKeyList(bag.requiredInfields || bag.required_infields),
  }
}

export function adminCatalogInfields(callType) {
  return normalizeCallType(callType) === AI_CALL_TYPES.DRIVE
    ? [...ADMIN_DEFAULT_DRIVE_INFIELDS]
    : [...ADMIN_DEFAULT_SCREENING_INFIELDS]
}

export function adminCatalogOutcomes(callType) {
  return normalizeCallType(callType) === AI_CALL_TYPES.DRIVE
    ? ADMIN_DEFAULT_DRIVE_OUTCOMES.map(item => ({ ...item }))
    : ADMIN_DEFAULT_SCREENING_OUTCOMES.map(item => ({ ...item }))
}

/**
 * Configured keys from ATS/admin, then the in-memory catalog, then official
 * fallbacks so prod create still works before anyone saves Fields.
 */
export function resolveConfiguredInfieldKeys(config, callType) {
  const parsed = parseAiCallingSchemaConfig(config)
  const ct = normalizeCallType(callType)
  if (ct === AI_CALL_TYPES.DRIVE && parsed.driveInfields.length) return parsed.driveInfields
  if (ct !== AI_CALL_TYPES.DRIVE && parsed.screeningInfields.length) return parsed.screeningInfields
  if (parsed.infields.length) return parsed.infields
  return getCreateInfieldsForCallType(ct, {
    screening: ADMIN_DEFAULT_SCREENING_INFIELDS,
    drive: ADMIN_DEFAULT_DRIVE_INFIELDS,
  }).map(field => field.key)
}

export function resolveConfiguredOutcomeSchema(config, callType) {
  const parsed = parseAiCallingSchemaConfig(config)
  const ct = normalizeCallType(callType)
  if (ct === AI_CALL_TYPES.DRIVE && parsed.driveOutcomes.length) return parsed.driveOutcomes
  if (ct !== AI_CALL_TYPES.DRIVE && parsed.screeningOutcomes.length) return parsed.screeningOutcomes
  if (parsed.outcomes.length) return parsed.outcomes
  return getCreateOutcomesForCallType(ct, {
    screening: ADMIN_DEFAULT_SCREENING_OUTCOMES,
    drive: ADMIN_DEFAULT_DRIVE_OUTCOMES,
  })
}

export function buildConfiguredCallInfieldSchema(config, callType, extraKeys = []) {
  const configured = resolveConfiguredInfieldKeys(config, callType)
  const extras = asKeyList(extraKeys)
  const keys = configured.length ? configured : extras
  const limit = getSchemaSlotLimit(INDUS_SCHEMA_SLOT_LIMIT)
  if (keys.length > limit) {
    throw new AiCallingConfigError(AI_CALLING_CONFIG_MESSAGES.createInfieldsLimit)
  }
  const schema = []
  const seen = new Set()
  keys.forEach((key) => {
    const item = buildOfficialCallInfield(key)
    if (!item || seen.has(item.field_name)) return
    seen.add(item.field_name)
    schema.push(item)
  })
  return schema
}

export function buildConfiguredCallOutcomeSchema(config, callType, extraOutcomes = []) {
  const configured = resolveConfiguredOutcomeSchema(config, callType)
  const extras = asOutcomeList(extraOutcomes)
  const list = configured.length ? configured : extras
  const limit = getSchemaSlotLimit(INDUS_SCHEMA_SLOT_LIMIT)
  if (list.length > limit) {
    throw new AiCallingConfigError(AI_CALLING_CONFIG_MESSAGES.createOutcomesLimit)
  }
  return list.slice()
}

/**
 * Language for create/initiate. No `en`, no agent-name heuristic, no Multi.
 * Priority: per-agent Admin map → current form → org config.
 */
export function resolveConfiguredLanguage(input = {}) {
  const src = asObject(input)
  const map = src.agentVoiceMap || src.agent_voice_map || {}
  const agentId = String(src.agent_id || src.agentId || '').trim()
  if (hasStoredAgentVoice(map, agentId)) {
    const stored = asObject(map[agentId])
    const fromMap = normalizeTranscriptLanguage(
      stored.language || stored.transcript_language || stored.transcriptLanguage,
    )
    if (fromMap === 'multi') return ''
    if (fromMap) return fromMap
  }
  const agentConfig = asObject(src.agent_config)
  const stt = asObject(agentConfig.stt_config)
  const explicit = normalizeTranscriptLanguage(
    src.transcript_language
    || src.transcriptLanguage
    || src.language
    || src.formLanguage
    || agentConfig.language
    || agentConfig.transcript_language
    || stt.language,
  )
  if (explicit === 'multi') return ''
  if (explicit) return explicit
  const tts = asObject(agentConfig.tts_config)
  const fromVoice = normalizeTranscriptLanguage(languageFromIndusVoice(
    src.voice
    || src.voice_id
    || agentConfig.voice
    || agentConfig.voice_id
    || tts.voice_id
    || tts.voice,
  ))
  if (fromVoice && fromVoice !== 'multi') return fromVoice
  const parsed = parseAiCallingSchemaConfig(src.config || src)
  return parsed.language
}

/** Voice from form / agent map / org config. Never invent Indus-multi-indu. */
export function resolveConfiguredVoice(input = {}) {
  const src = asObject(input)
  const agentConfig = asObject(src.agent_config)
  const tts = asObject(agentConfig.tts_config)
  const explicit = canonicalizeIndusVoiceId(
    src.voice
    || src.voice_id
    || src.voiceId
    || src.formVoice
    || agentConfig.voice
    || agentConfig.voice_id
    || tts.voice_id
    || tts.voice,
  )
  if (explicit) return explicit
  const map = src.agentVoiceMap || src.agent_voice_map || {}
  const agentId = String(src.agent_id || src.agentId || '').trim()
  if (hasStoredAgentVoice(map, agentId)) {
    const stored = asObject(map[agentId])
    const fromMap = canonicalizeIndusVoiceId(stored.voice || stored.voice_id || stored.voiceId)
    if (fromMap) return fromMap
  }
  return parseAiCallingSchemaConfig(src.config || src).voice
}

export function lookupAliasedValue(values, key, aliases = {}) {
  const src = asObject(values)
  const direct = src[key]
  if (direct != null && String(direct).trim()) return String(direct).trim()
  const list = Array.isArray(aliases[key])
    ? aliases[key]
    : (aliases[key] ? [aliases[key]] : [])
  for (let i = 0; i < list.length; i += 1) {
    const alias = String(list[i] || '').trim()
    if (!alias || alias === key) continue
    const val = src[alias]
    if (val != null && String(val).trim()) return String(val).trim()
  }
  return ''
}

export function requiredInfieldKeys(config, schemaKeys = []) {
  const parsed = parseAiCallingSchemaConfig(config)
  if (parsed.requiredInfields.length) {
    return parsed.requiredInfields.filter(key => !schemaKeys.length || schemaKeys.includes(key))
  }
  if (schemaKeys.includes('candidate_name')) return ['candidate_name']
  return []
}

export function validateCreateAgentInput(input = {}) {
  const src = asObject(input)
  const agentIn = asObject(src.agent_in)
  const agentConfig = asObject(src.agent_config)
  const name = firstNonEmpty(
    agentIn.agent_name, agentIn.name, src.agent_name, src.name,
  )
  const language = resolveConfiguredLanguage(src)
  const rawLang = normalizeTranscriptLanguage(
    src.language || src.transcript_language || agentConfig.language,
  )
  const voice = resolveConfiguredVoice({
    ...src,
    voice: src.voice || src.voice_id || agentConfig.voice || agentConfig.voice_id,
  })
  const firstMessage = firstNonEmpty(
    agentConfig.starting_instructions,
    src.starting_instructions,
    src.firstMessage,
    src.first_message,
  )
  const systemPrompt = firstNonEmpty(
    agentConfig.system_prompt,
    src.system_prompt,
    src.systemPrompt,
    src.previewSystemPrompt,
  )
  const callType = src.callType || src.call_type
  let infields = Array.isArray(src.call_infield) ? src.call_infield : []
  let outcomes = Array.isArray(src.call_outcome) ? src.call_outcome : []
  if (!infields.length) {
    infields = buildConfiguredCallInfieldSchema(src.config, callType)
  }
  if (!outcomes.length) {
    outcomes = buildConfiguredCallOutcomeSchema(src.config, callType)
  }

  const errors = []
  if (!name) errors.push(AI_CALLING_CONFIG_MESSAGES.createName)
  if (rawLang === 'multi') errors.push(AI_CALLING_CONFIG_MESSAGES.createLanguageMulti)
  else if (!language) errors.push(AI_CALLING_CONFIG_MESSAGES.createLanguage)
  if (!voice) errors.push(AI_CALLING_CONFIG_MESSAGES.createVoice)
  if (!firstMessage || !systemPrompt) errors.push(AI_CALLING_CONFIG_MESSAGES.createScript)
  if (!infields.length) errors.push(AI_CALLING_CONFIG_MESSAGES.createInfields)
  if (!outcomes.length) errors.push(AI_CALLING_CONFIG_MESSAGES.createOutcomes)
  const slotLimit = getSchemaSlotLimit(INDUS_SCHEMA_SLOT_LIMIT)
  if (infields.length > slotLimit) {
    errors.push(AI_CALLING_CONFIG_MESSAGES.createInfieldsLimit)
  }
  if (outcomes.length > slotLimit) {
    errors.push(AI_CALLING_CONFIG_MESSAGES.createOutcomesLimit)
  }
  return {
    ok: errors.length === 0,
    errors,
    name,
    language,
    voice,
    infields,
    outcomes,
  }
}

export function validateInitiateCallInput(input = {}, built = {}) {
  const src = asObject(input)
  const language = firstNonEmpty(
    built.transcript_language,
    resolveConfiguredLanguage(src),
    'en',
  )
  const rawLang = normalizeTranscriptLanguage(
    src.transcript_language || src.transcriptLanguage || src.language,
  )
  const agentId = firstNonEmpty(
    built.agent_number,
    built.agent_id,
    src.agent_number,
    src.agent_id,
    src.agentId,
  )
  const customerNumber = firstNonEmpty(
    built.customer_number,
    src.customer_number,
    src.contact_no,
    src.contactNo,
  )
  const callType = src.callType || src.call_type
  const declared = Array.isArray(src.declared_infields || src.declaredInfields)
    ? asKeyList(src.declared_infields || src.declaredInfields)
    : []
  const schemaKeys = declared.length
    ? declared
    : resolveConfiguredInfieldKeys(src.config, callType)
  const callInfields = asObject(built.call_infields)
  const required = requiredInfieldKeys(src.config, schemaKeys)
  const missingRequired = required.filter(key => !lookupAliasedValue(
    { ...asObject(src.call_infields), ...asObject(src.callInfields), ...callInfields },
    key,
    parseAiCallingSchemaConfig(src.config).infieldAliases,
  ))

  const errors = []
  const missing = []
  if (!agentId) {
    errors.push(AI_CALLING_CONFIG_MESSAGES.initiateAgent)
    missing.push('agent')
  }
  if (!customerNumber) {
    errors.push(AI_CALLING_CONFIG_MESSAGES.initiatePhone)
    missing.push('phone')
  }
  if (rawLang === 'multi') errors.push(AI_CALLING_CONFIG_MESSAGES.initiateLanguageMulti)
  else if (!language) errors.push(AI_CALLING_CONFIG_MESSAGES.initiateLanguage)
  if (!schemaKeys.length && !Object.keys(callInfields).length && !Object.keys(asObject(src.call_infields)).length) {
    errors.push(AI_CALLING_CONFIG_MESSAGES.initiateInfields)
  }
  if (missingRequired.length) {
    errors.push(`${AI_CALLING_CONFIG_MESSAGES.initiateRequired}: ${missingRequired.join(', ')}.`)
    missing.push(...missingRequired)
  }
  return {
    ok: errors.length === 0,
    errors,
    missing,
    language,
    schemaKeys,
  }
}

export function assertCreateReady(input = {}) {
  const result = validateCreateAgentInput(input)
  if (!result.ok) {
    throw new AiCallingConfigError(result.errors[0], { errors: result.errors })
  }
  return result
}

export function assertInitiateReady(input = {}, built = {}) {
  const result = validateInitiateCallInput(input, built)
  if (!result.ok) {
    throw new AiCallingConfigError(result.errors[0], {
      errors: result.errors,
      missing: result.missing,
    })
  }
  return result
}

/** Persistable admin defaults when the settings form first opens with empty lists. */
export function seedAdminSchemaForm(form = {}) {
  const parsed = parseAiCallingSchemaConfig(form)
  return {
    ...form,
    screeningInfields: parsed.screeningInfields.length
      ? parsed.screeningInfields
      : [...ADMIN_DEFAULT_SCREENING_INFIELDS],
    driveInfields: parsed.driveInfields.length
      ? parsed.driveInfields
      : [...ADMIN_DEFAULT_DRIVE_INFIELDS],
    screeningOutcomes: parsed.screeningOutcomes.length
      ? parsed.screeningOutcomes
      : ADMIN_DEFAULT_SCREENING_OUTCOMES.map(item => ({ ...item })),
    driveOutcomes: parsed.driveOutcomes.length
      ? parsed.driveOutcomes
      : ADMIN_DEFAULT_DRIVE_OUTCOMES.map(item => ({ ...item })),
    infieldAliases: parsed.infieldAliases,
    requiredInfields: parsed.requiredInfields.length
      ? parsed.requiredInfields
      : ['candidate_name'],
  }
}
